import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';
import { SENTRY_CONFIG_DATA_TYPE } from 'src/constants/type';
import { ensureRemoteConfigFirebaseApp } from 'src/lib/firebaseConfig';

import { LocalStorageInstance } from './localStroage/LocalStorageService';
import { CUSTOM_EVENT_NAMES, pubSub } from './pubSub';

const app = ensureRemoteConfigFirebaseApp();
const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = 1500;

let orgConfigValue: string | undefined;
let cachedFirebaseData: Record<string, any> | null = null;

const firebaseInitPromise: Promise<Record<string, any> | null> = fetchAndActivate(remoteConfig)
  .then(async () => {
    console.log('Remote Config values activated', remoteConfig);

    const isUsocketEnabledInChat = getValueFromFirebase(
      process.env.REACT_APP_USOCKET_CHAT_FIREBASE,
    );
    orgConfigValue = getValueFromFirebase('org_config');
    const isUsocketEnabledInPlayer = getValueFromFirebase(
      process.env.REACT_APP_USOCKET_PLAYER_FIREBASE,
    );
    const isMqttEnabled = getValueFromFirebase(process.env.REACT_APP_MQTT_CHAT_FIREBASE);
    const isMqttEmojiEnabled = getValueFromFirebase(process.env.REACT_APP_MQTT_MQTT_FIREBASE);
    const sentryFireBaseConfig = getValueFromFirebase(
      process.env.REACT_APP_IS_SENTRY_ENABLED_FIREBASE ?? '',
    );
    const sentryEnv: string = process.env.REACT_APP_ENVIRONMENT ?? '';
    console.log('sentry', 'env', { sentryEnv }, JSON.parse(sentryFireBaseConfig));
    const sentryConfigData: SENTRY_CONFIG_DATA_TYPE = JSON.parse(sentryFireBaseConfig)[
      sentryEnv
    ] || { enabled: false, sampleRate: 0 };

    const postPollFeatureConfig =
      getValueFromFirebase(process.env.REACT_APP_POLL_FEATURE_TOOLS_CONFIG ?? '') || '{}';

    const postPollFeatureConfigData = JSON.parse(postPollFeatureConfig);

    const awsHlsLiveStreamLatencyTimeInSecond =
      +getValueFromFirebase(process.env.REACT_APP_AWS_HLS_LIVE_STREAM_LATENCY_SECONDS ?? '') || 25;

    const firebaseData = {
      isUsocketEnabledInChat,
      isMqttEmojiEnabled,
      isUsocketEnabledInPlayer,
      isMqttEnabled,
      sentryConfigData,
      postPollFeatureConfigData,
      awsHlsLiveStreamLatencyTimeInSecond: Number(awsHlsLiveStreamLatencyTimeInSecond),
    };
    console.log('firebaseData', firebaseData);
    cachedFirebaseData = firebaseData;
    pubSub.publish(CUSTOM_EVENT_NAMES.GOT_FIREBASE_CONFIG, firebaseData);
    const NotesEnabledBatchIds = getValueFromFirebase('notes_enabled_batch_Ids_prod');
    LocalStorageInstance.setNotesEnabledBatchIds(JSON.stringify(NotesEnabledBatchIds));
    return firebaseData;
  })
  .catch((err: unknown) => {
    console.log('Failed to fetch and activate Remote Config:', err);
    return null;
  });

export const getOrgConfig = async (): Promise<string | undefined> => {
  await firebaseInitPromise;
  return orgConfigValue;
};

export { firebaseInitPromise };

export const getFirebaseConfigData = (): Record<string, any> | null => cachedFirebaseData;

export const getValueFromFirebase = (key?: string): string => {
  const trimmedKey = key?.trim();
  if (!trimmedKey) return '';

  const configValue = getValue(remoteConfig, trimmedKey);

  // asString() returns '' for boolean-type RC parameters
  const stringValue = configValue.asString().trim();
  if (stringValue !== '') return stringValue;

  // Fall back to asBoolean for boolean-typed parameters
  try {
    return String(configValue.asBoolean());
  } catch {
    return stringValue;
  }
};

export const getVideoCopilotOnboardingDataFromFirebase = async () => {
  try {
    await firebaseInitPromise;

    const vcGreetingMessage = getValueFromFirebase('vc_greeting_message');
    const vcSecondtimeGreetingMessage = getValueFromFirebase('vc_secondtime_greeting_message');
    const vcFirstTimeAnnotationDrawMessage = getValueFromFirebase(
      'vc_first_time_annotation_draw_message',
    );
    const vcFirstTimeAudioMessage = getValueFromFirebase('vc_first_time_audio_message');
    const vcSecondTimeAnnotationInfoMessage = getValueFromFirebase(
      'vc_second_time_annotation_info_message',
    );
    const vcSecondTimeAnnotationDrawMessage = getValueFromFirebase(
      'vc_second_time_annotation_draw_message',
    );
    const vcSecondTimeAudioMessage = getValueFromFirebase('vc_second_time_audio_message');

    const onboardingData = {
      vcGreetingMessage,
      vcSecondtimeGreetingMessage,
      vcFirstTimeAnnotationDrawMessage,
      vcFirstTimeAudioMessage,
      vcSecondTimeAnnotationInfoMessage,
      vcSecondTimeAnnotationDrawMessage,
      vcSecondTimeAudioMessage,
    };

    return onboardingData;
  } catch (error) {
    console.error('Error fetching Video Copilot onboarding data from Firebase:', error);
    throw error;
  }
};

export const getAdsDataFromFirebase = async () => {
  try {
    await firebaseInitPromise;
    const data = await getValueFromFirebase('video_ads');
    return JSON.parse(data);
  } catch (error) {
    throw error;
  }
};
